import {
  Box,
  Flex,
  Text,
  IconButton,
  Button as ChakraButton,
  VStack,
  HStack,
  useColorModeValue,
  Icon,
} from "@chakra-ui/react";
import React, { useState, useMemo, useEffect } from "react";
import colors from "../../utils/colors";
import Dropdown from "../../components/ui/dropdown";
import { useFormik } from "formik";
import * as yup from "yup";
import { daysAbbr, hourOfDay } from "../patients/utils";
import { v4 as uuidv4 } from "uuid";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useUpdateDoctor } from "../../apis/doctor";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { setDoctorInfo } from "../../store/authSlice";
import { TDoctorInfo, TAvailableTimeSlot } from "../../apis/types";
import { EditIcon, TimeIcon } from "@chakra-ui/icons";
import customToast, { ToastType } from "../../components/Toast";

interface FormAvailabilitySlot extends TAvailableTimeSlot {
  formId: string;
}

const AvailabilityCard = () => {
  const { doctorInfo } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const { mutate: updateDoctor, isPending } = useUpdateDoctor();
  const [isEditing, setIsEditing] = useState(false);

  const textColor = useColorModeValue(colors.dark, "gray.100");
  const subtleTextColor = useColorModeValue("gray.600", "gray.300");
  const labelColor = useColorModeValue(colors.dark, "gray.200");
  const displayItemBorderColor = useColorModeValue("gray.200", "gray.600");
  const editItemBorderColor = useColorModeValue("gray.200", "gray.600");

  const mapApiToFormSlots = (
    slots?: TAvailableTimeSlot[]
  ): FormAvailabilitySlot[] => {
    if (slots && slots.length > 0) {
      return slots.map((item) => ({
        ...item,
        formId: item.id || uuidv4(),
        day: item.day,
        startTime: item.startTime,
        endTime: item.endTime,
      }));
    }
    return [{ formId: uuidv4(), day: null, startTime: null, endTime: null }];
  };

  const initialFormSlots = useMemo(() => {
    return mapApiToFormSlots(doctorInfo?.availability);
  }, [doctorInfo?.availability]);

  const formik = useFormik<{ availability: FormAvailabilitySlot[] }>({
    initialValues: {
      availability: initialFormSlots,
    },
    validationSchema: yup.object({
      availability: yup
        .array()
        .of(
          yup.object().shape({
            day: yup.string().required("Day is required").nullable(),
            startTime: yup
              .string()
              .required("Start time is required")
              .nullable(),

            endTime: yup
              .string()
              .required("End time is required")
              .nullable()
              .test(
                "is-after-start-time",
                "End time must be after start time",
                function (value) {
                  const { startTime } = this.parent;
                  if (!startTime || !value) return true;

                  const parseTime = (timeStr: string) => {
                    const [hourMin, modifier] = timeStr.split(/(am|pm)/i);
                    let [hours, minutes] = hourMin.split(":").map(Number);

                    if (modifier.toLowerCase() === "pm" && hours !== 12)
                      hours += 12;
                    if (modifier.toLowerCase() === "am" && hours === 12)
                      hours = 0;

                    return hours * 60 + minutes; // total minutes
                  };

                  return parseTime(value) > parseTime(startTime);
                }
              ),

            formId: yup.string().required(),
            id: yup.string().optional(),
          })
        )
        .min(1, "At least one availability slot is required.")
        .required("Availability is required"),
    }),
    onSubmit: (values) => {
      const slotsToSave: TAvailableTimeSlot[] = values.availability.map(
        (slot) => {
          const { formId, ...apiSlot } = slot;
          return apiSlot as TAvailableTimeSlot;
        }
      );

      updateDoctor(
        {
          data: { availability: slotsToSave },
          doctorId: doctorInfo?._id,
        },
        {
          onSuccess: (response: any) => {
            customToast("Availability updated!", ToastType.success);
            const returnedSlotsFromApi = response?.data?.availability;

            const updatedAvailableTimeForStore =
              returnedSlotsFromApi && Array.isArray(returnedSlotsFromApi)
                ? returnedSlotsFromApi
                : slotsToSave.map((s) => ({ ...s, id: s.id || uuidv4() }));

            const updatedDoctorData = {
              ...doctorInfo,
              availability: updatedAvailableTimeForStore,
            } as TDoctorInfo;
            dispatch(setDoctorInfo(updatedDoctorData));

            const sessionDoc = sessionStorage.getItem("created-doctor");
            if (sessionDoc) {
              try {
                const parsedSessionDoc = JSON.parse(sessionDoc) as TDoctorInfo;
                const updatedSessionDoc = {
                  ...parsedSessionDoc,
                  availability: updatedAvailableTimeForStore,
                };
                sessionStorage.setItem(
                  "created-doctor",
                  JSON.stringify(updatedSessionDoc)
                );
              } catch (e) {
                console.error(
                  "Error updating session storage for availability",
                  e
                );
              }
            }
            setIsEditing(false);
          },
          onError: (error: any) => {
            customToast(
              error?.response?.data?.message || "Update failed.",
              ToastType.error
            );
          },
        }
      );
    },
  });

  const hasChanges = useMemo(() => {
    const initial = mapApiToFormSlots(doctorInfo?.availability);
    return (
      JSON.stringify(initial) !== JSON.stringify(formik.values.availability)
    );
  }, [formik.values.availability, doctorInfo?.availability]);

  useEffect(() => {
    if (isEditing) {
      formik.resetForm({
        values: { availability: mapApiToFormSlots(doctorInfo?.availability) },
      });
    }
  }, [isEditing, doctorInfo?.availability]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    formik.resetForm({
      values: { availability: mapApiToFormSlots(doctorInfo?.availability) },
    });
  };

  const handleRemoveSlot = (formIdToRemove: string) => {
    let filteredSlots = formik.values.availability.filter(
      (slot) => slot.formId !== formIdToRemove
    );
    if (filteredSlots.length === 0) {
      filteredSlots = [
        { formId: uuidv4(), day: null, startTime: null, endTime: null },
      ];
    }
    formik.setFieldValue("availability", filteredSlots);
  };

  const handleAddNewSlot = () => {
    const newSlot: FormAvailabilitySlot = {
      formId: uuidv4(),
      day: null,
      startTime: null,
      endTime: null,
    };
    formik.setFieldValue("availability", [
      ...formik.values.availability,
      newSlot,
    ]);
  };

  if (!isEditing) {
    return (
      <Box>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Text
            fontFamily="lato"
            fontWeight="bold"
            fontSize="xl"
            color={textColor}
          >
            Availability
          </Text>
          <IconButton
            aria-label="Edit availability"
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            onClick={handleEditClick}
          />
        </Flex>
        {doctorInfo?.availability &&
        doctorInfo.availability.length > 0 &&
        doctorInfo.availability.some(
          (s) => s.day && s.startTime && s.endTime
        ) ? (
          <VStack spacing={3} align="stretch">
            {doctorInfo.availability
              .filter((s) => s.day && s.startTime && s.endTime)
              .map((slot, index) => (
                <HStack
                  key={slot.id || index}
                  p={2}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={displayItemBorderColor}
                  justifyContent="space-between"
                >
                  <HStack>
                    <Icon as={TimeIcon} color={subtleTextColor} />
                    <Text fontWeight="medium" color={textColor}>
                      {slot.day}:
                    </Text>
                  </HStack>
                  <Text color={subtleTextColor}>
                    {slot.startTime} - {slot.endTime}
                  </Text>
                </HStack>
              ))}
          </VStack>
        ) : (
          <Text color={subtleTextColor}>No availability slots added yet.</Text>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Text
        fontFamily="lato"
        fontWeight="bold"
        fontSize="xl"
        color={textColor}
        mb={4}
      >
        Edit Availability
      </Text>
      <form onSubmit={formik.handleSubmit}>
        <VStack spacing={5} align="stretch">
          {formik.values.availability.map((val, index) => (
            <Flex
              key={val.formId}
              direction={{ base: "column", md: "row" }}
              alignItems={{ base: "stretch", md: "flex-end" }}
              gap={{ base: 3, md: 2 }}
              pb={formik.values.availability.length > 1 ? 3 : 0}
              borderBottomWidth={
                index < formik.values.availability.length - 1 ? "1px" : "0"
              }
              borderColor={editItemBorderColor}
            >
              <Box flex={{ base: "1 1 100%", md: "1 1 auto" }}>
                <Text
                  fontFamily="lato"
                  fontSize="sm"
                  fontWeight={400}
                  color={labelColor}
                  mb="8px"
                >
                  Day
                </Text>
                <Dropdown
                  options={daysAbbr}
                  onSelect={(e) =>
                    formik.setFieldValue(`availability[${index}].day`, e)
                  }
                  placeholder="Select Day"
                  className="!h-[40px] w-full !pl-[12px]"
                  value={val.day ?? ""}
                />
                {formik.touched.availability?.[index]?.day &&
                  formik.errors.availability?.[index]?.["day"] && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                      {formik.errors.availability?.[index]?.["day"]}
                    </Text>
                  )}
              </Box>
              <Box flex={{ base: "1 1 100%", md: "1 1 auto" }}>
                <Text
                  fontFamily="lato"
                  fontSize="sm"
                  fontWeight={400}
                  color={labelColor}
                  mb="8px"
                >
                  Start Time
                </Text>
                <Dropdown
                  options={hourOfDay}
                  onSelect={(e) =>
                    formik.setFieldValue(`availability[${index}].startTime`, e)
                  }
                  placeholder="Select Start Time"
                  className="!h-[40px] w-full !pl-[12px]"
                  value={val.startTime ?? ""}
                />
                {formik.touched.availability?.[index]?.startTime &&
                  formik.errors.availability?.[index]?.["startTime"] && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                      {formik.errors.availability?.[index]?.["startTime"]}
                    </Text>
                  )}
              </Box>
              <Box flex={{ base: "1 1 100%", md: "1 1 auto" }}>
                <Text
                  fontFamily="lato"
                  fontSize="sm"
                  fontWeight={400}
                  color={labelColor}
                  mb="8px"
                >
                  End Time
                </Text>
                <Dropdown
                  options={hourOfDay}
                  onSelect={(e) =>
                    formik.setFieldValue(`availability[${index}].endTime`, e)
                  }
                  placeholder="Select End Time"
                  className="!h-[40px] w-full !pl-[12px]"
                  value={val.endTime ?? ""}
                />
                {formik.touched.availability?.[index]?.endTime &&
                  formik.errors.availability?.[index]?.["endTime"] && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                      {formik.errors.availability?.[index]?.["endTime"]}
                    </Text>
                  )}
              </Box>
              {formik.values.availability.length > 1 && (
                <IconButton
                  aria-label="Remove availability slot"
                  icon={
                    <Text
                      color={colors.red}
                      fontWeight="semibold"
                      fontSize="sm"
                    >
                      X
                    </Text>
                  }
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemoveSlot(val.formId)}
                  alignSelf={{ base: "flex-end", md: "flex-end" }}
                  mb={{ base: 0, md: "1px" }}
                />
              )}
            </Flex>
          ))}
        </VStack>
        <ChakraButton
          mt={4}
          size="sm"
          colorScheme="teal"
          variant="outline"
          onClick={handleAddNewSlot}
          isDisabled={isPending}
        >
          + Add New Slot
        </ChakraButton>
        <Flex gap={3} justifyContent="flex-end" mt={6}>
          <ChakraButton
            variant="ghost"
            onClick={handleCancelEdit}
            size="sm"
            isDisabled={isPending}
          >
            Cancel
          </ChakraButton>

          <ChakraButton
            bg={colors.primary}
            color={colors.white}
            _hover={{ bg: colors.secondaryBlack || colors.primary }}
            type="submit"
            size="sm"
            isLoading={isPending}
            isDisabled={isPending || !hasChanges || !formik.isValid}
          >
            Save Changes
          </ChakraButton>
        </Flex>
      </form>
    </Box>
  );
};

export default AvailabilityCard;
